import apiClient from "./apiClient";
import { STORAGE_BASE_URL } from "../constants";
import {
  convertArrayRefsToFileService,
  convertFileServiceImagesToArrayRefs,
  processDescriptionImages,
} from "./taskUtils";

const ARRAY_REF_REGEX = /\[([0-9a-fA-F-]{36})\s*,\s*(\d+)\s*,\s*(\d+)\]/g;
const FILE_SERVICE_IMG_REGEX = /<img\b[^>]*file-service:\/\/([0-9a-fA-F-]{36})[^>]*>/gi;

export const normalizeUrl = (url = "") => (url ? String(url).split("?")[0] : "");

export const createRichTextImageMaps = () => ({
  byUrl: new Map(),
  byUrlBase: new Map(),
});

export const clearRichTextImageMaps = (maps) => {
  maps?.byUrl?.clear();
  maps?.byUrlBase?.clear();
};

const resolveResponseUrl = (payload, visited = new WeakSet()) => {
  if (!payload) return null;
  if (typeof payload === "string") return payload;
  if (typeof payload !== "object") return null;
  if (visited.has(payload)) return null;
  visited.add(payload);

  if (Array.isArray(payload)) {
    for (const item of payload) {
      const resolved = resolveResponseUrl(item, visited);
      if (resolved) return resolved;
    }
    return null;
  }

  for (const field of ["uri", "url", "downloadUrl", "download_url", "signedUrl"]) {
    if (typeof payload[field] === "string" && payload[field].trim()) {
      return payload[field];
    }
  }

  for (const value of Object.values(payload)) {
    const resolved = resolveResponseUrl(value, visited);
    if (resolved) return resolved;
  }

  return null;
};

export const fetchRichTextSignedImageUrl = async (fileId) => {
  if (!fileId) return null;

  try {
    const response = await apiClient.get(
      `${STORAGE_BASE_URL}/api/v1/files/photo/signed-urls?fids=${encodeURIComponent(fileId)}`,
    );
    return resolveResponseUrl(response?.data);
  } catch (error) {
    console.error("[richTextImages] failed to fetch signed image URL", {
      fileId,
      error: error?.message,
    });
    return null;
  }
};

export const getImageDimensions = (file) =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({
        width: img.naturalWidth || img.width || 1,
        height: img.naturalHeight || img.height || 1,
      });
    };
    img.onerror = (error) => {
      URL.revokeObjectURL(objectUrl);
      reject(error);
    };
    img.src = objectUrl;
  });

const extractStoredImageRefs = (html = "") => {
  const refs = [];
  const normalizedHtml = convertArrayRefsToFileService(html || "");

  let match;
  while ((match = ARRAY_REF_REGEX.exec(html || "")) !== null) {
    refs.push({
      fileId: match[1],
      width: Number(match[2]) || 1,
      height: Number(match[3]) || 1,
    });
  }

  while ((match = FILE_SERVICE_IMG_REGEX.exec(normalizedHtml)) !== null) {
    const tag = match[0];
    const widthMatch = tag.match(/width=["']?(\d+)["']?/i);
    const heightMatch = tag.match(/height=["']?(\d+)["']?/i);
    refs.push({
      fileId: match[1],
      width: widthMatch ? Number(widthMatch[1]) || 1 : 1,
      height: heightMatch ? Number(heightMatch[1]) || 1 : 1,
    });
  }

  const seen = new Set();
  return refs.filter((ref) => {
    const key = `${ref.fileId}:${ref.width}:${ref.height}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const extractImageSources = (html = "") =>
  [...String(html || "").matchAll(/<img\b[^>]*src=["']([^"']+)["'][^>]*>/gi)]
    .map((match) => match[1])
    .filter(Boolean);

export const rememberRichTextImage = (maps, src, info) => {
  if (!maps || !src || !info?.fileId) return;
  const normalizedInfo = {
    fileId: info.fileId,
    width: Number(info.width) || 1,
    height: Number(info.height) || 1,
  };
  maps.byUrl.set(src, normalizedInfo);
  maps.byUrlBase.set(normalizeUrl(src), normalizedInfo);
  maps.byUrl.set(`file-service://${normalizedInfo.fileId}`, normalizedInfo);
};

export const processRichTextImagesForEdit = async (html = "", maps) => {
  const rawHtml = html || "";
  if (!rawHtml) return "";

  const refs = extractStoredImageRefs(rawHtml);
  const processedHtml = await processDescriptionImages(rawHtml, refs);
  const sources = extractImageSources(processedHtml);

  refs.forEach((ref, index) => {
    const src = sources[index];
    if (src) rememberRichTextImage(maps, src, ref);
  });

  return processedHtml || convertArrayRefsToFileService(rawHtml);
};

export const normalizeRichTextImagesForSave = (html = "", maps) => {
  if (!html) return html;

  const normalizedHtml = String(html).replace(
    /<img\b[^>]*src=["']([^"']+)["'][^>]*>/gi,
    (imgTag, src) => {
      if (src?.startsWith("file-service://")) return imgTag;
      const info = maps?.byUrl?.get(src) || maps?.byUrlBase?.get(normalizeUrl(src));
      if (!info?.fileId) return imgTag;

      const widthMatch = imgTag.match(/width=["']?(\d+)["']?/i);
      const heightMatch = imgTag.match(/height=["']?(\d+)["']?/i);
      const width = info.width || (widthMatch ? Number(widthMatch[1]) : 1) || 1;
      const height = info.height || (heightMatch ? Number(heightMatch[1]) : 1) || 1;
      return `[${info.fileId}, ${width}, ${height}]`;
    },
  );

  return convertFileServiceImagesToArrayRefs(normalizedHtml);
};

export const uploadRichTextImage = async ({
  file,
  helpers,
  uploadFile,
  userId,
  maps,
  onHtmlChange,
  purpose = "TASK_PHOTOS",
}) => {
  const {
    insertPlaceholder,
    replacePlaceholderWithText,
    replacePlaceholderWithImage,
    getHtml,
  } = helpers;

  const extension = file.type?.split("/")[1] || "png";
  const filename =
    file.name && file.name.trim().length > 0
      ? file.name
      : `pasted-${Date.now()}.${extension}`;
  const uploadFileObject = file.name ? file : new File([file], filename, {
    type: file.type || "image/png",
  });

  const placeholderRange = insertPlaceholder(`Uploading ${filename}... please wait`);
  onHtmlChange?.(getHtml());

  try {
    let dimensions = { width: 1, height: 1 };
    try {
      dimensions = await getImageDimensions(uploadFileObject);
    } catch (error) {
      console.warn("[richTextImages] failed to read image dimensions", error);
    }

    const uploadResult = await uploadFile(userId, uploadFileObject, {
      purpose,
      fileType: "PHOTO",
    });
    const fileId = uploadResult?.fileId;
    if (!fileId) throw new Error("Upload finished without a file id.");

    const signedUrl =
      (await fetchRichTextSignedImageUrl(fileId)) ||
      uploadResult?.meta?.url ||
      uploadResult?.url ||
      `file-service://${fileId}`;
    const info = {
      fileId,
      width: dimensions.width || 1,
      height: dimensions.height || 1,
    };

    rememberRichTextImage(maps, signedUrl, info);
    replacePlaceholderWithImage(placeholderRange, signedUrl, {
      width: info.width,
      height: info.height,
      alt: filename.replace(/\.[^/.]+$/, "") || "image",
    });
    onHtmlChange?.(getHtml());
  } catch (error) {
    console.error("[richTextImages] inline image upload failed", error);
    replacePlaceholderWithText(placeholderRange, "(image upload failed)");
    onHtmlChange?.(getHtml());
    throw error;
  }
};
